/*
 * ew-consent.js — Consent Mode v2 banner behavior (G-1 Commit A)
 *
 * Companion to snippets/ew-consent-banner.liquid + assets/ew-consent.css.
 *
 * Role
 *   The <head> in layout/theme.liquid sets region-scoped Consent Mode v2
 *   DEFAULTS (denied in EEA/UK/CH/non-EU-EEA, granted elsewhere) BEFORE
 *   gtag('config',...). This file is the first-party opt-in/opt-out
 *   surface: on first visit it shows the banner and, on the visitor's
 *   choice, fires the Consent Mode v2 UPDATE for all four signals.
 *
 * Behavior
 *   - First visit (no stored choice) → unhide banner, move focus to it.
 *   - Accept all  → gtag('consent','update', { all four = 'granted' }).
 *   - Reject all  → gtag('consent','update', { all four = 'denied'  }).
 *   - Esc while banner is focused → treated as Reject (the safe default).
 *   - Choice persisted in localStorage; never re-prompted once chosen.
 *   - Non-modal: does NOT trap focus and does NOT block the page. On
 *     dismiss, focus returns to wherever it was before the banner showed.
 *
 * Guards
 *   - No banner element in DOM (blank ga4_measurement_id) → exits, no-op.
 *   - gtag missing → the choice is still persisted; the update is a no-op
 *     (no thrown error), so the banner never re-prompts.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'ew_consent_choice'; /* "granted" | "denied" */

  function readChoice() {
    try { return localStorage.getItem(STORAGE_KEY); }
    catch (_) { return null; }
  }
  function writeChoice(value) {
    try { localStorage.setItem(STORAGE_KEY, value); } catch (_) { /* private mode */ }
  }

  /* Push the Consent Mode v2 update (GA4) AND notify the shared onsite-vendor
     gate (BUG 5) so Clarity / Klaviyo / Meta / Reddit react to this choice
     without a reload. Both halves are safe if their target is absent. */
  /* Tell Shopify's Customer Privacy API about the choice. This is the ONLY
     mechanism that makes Shopify-MANAGED app pixels (the Klaviyo web pixel, and
     any future Meta/Reddit WEB pixel added through Shopify) honor consent — the
     theme-side ewConsent gate below cannot reach those, because they are injected
     by Shopify's web-pixel sandbox, not by our theme loaders. Guarded for
     existence: the API ships via consent-tracking-api.js on the storefront, but
     we never assume it's present. setTrackingConsent is async + callback-style;
     we pass a no-op callback. NOTE: this does NOT gate the klaviyo-onsite-embed
     APP BLOCK — that injects klaviyo.js independently and is an LP decision. */
  function setShopifyConsent(granted) {
    try {
      if (window.Shopify &&
          window.Shopify.customerPrivacy &&
          typeof window.Shopify.customerPrivacy.setTrackingConsent === 'function') {
        window.Shopify.customerPrivacy.setTrackingConsent({
          analytics: granted,
          marketing: granted,
          preferences: granted,
          sale_of_data: granted
        }, function () {});
      }
    } catch (_) { /* API absent or threw → no-op, choice still persisted */ }
  }

  function applyConsent(granted) {
    var state = granted ? 'granted' : 'denied';
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        ad_storage: state,
        ad_user_data: state,
        ad_personalization: state,
        analytics_storage: state
      });
    }
    /* Shopify-managed web pixels (Klaviyo web pixel, future Meta/Reddit web
       pixels) — gate via the Customer Privacy API. */
    setShopifyConsent(granted);
    /* Shared gate: drives clarity('consent',…) live and releases the withheld
       Clarity/Klaviyo/Meta/Reddit theme loaders on grant. ewConsent reads the
       same persisted ew_consent_choice flag; writeChoice() (below) has already
       stored it before choose() calls applyConsent(), so the gate's own reads
       agree. */
    if (window.ewConsent && typeof window.ewConsent.set === 'function') {
      window.ewConsent.set(granted);
    }
  }

  function init() {
    var banner = document.querySelector('[data-ew-consent]');
    if (!banner) return; /* no GA4 id → snippet not rendered → no-op */

    /* Already chose in a prior visit → re-assert the stored consent so
       this page-load reflects it, then stay hidden. */
    var prior = readChoice();
    if (prior === 'granted' || prior === 'denied') {
      applyConsent(prior === 'granted');
      return;
    }

    /* First-time visitor (no stored choice) → set Shopify's Customer Privacy
       default to DENIED before the banner is answered, mirroring the gate's
       default-deny posture. This keeps Shopify-managed web pixels gated in the
       sub-second window before the visitor chooses. (gtag/Consent Mode already
       defaults denied-by-region in <head>; ewConsent already defaults deny —
       this aligns the Shopify API to the same pre-choice posture.) */
    setShopifyConsent(false);

    var acceptBtn = banner.querySelector('[data-ew-consent-accept]');
    var rejectBtn = banner.querySelector('[data-ew-consent-reject]');
    var lastFocused = null;

    function show() {
      lastFocused = document.activeElement;
      banner.hidden = false;
      /* next frame so the unhide paints before the slide-up transition */
      requestAnimationFrame(function () { banner.classList.add('is-visible'); });
      /* move focus into the banner for keyboard + SR users */
      if (acceptBtn) {
        try { acceptBtn.focus({ preventScroll: true }); } catch (_) { acceptBtn.focus(); }
      }
    }

    function dismiss() {
      banner.classList.remove('is-visible');
      banner.hidden = true;
      /* return focus to where the user was before the banner appeared */
      if (lastFocused && typeof lastFocused.focus === 'function') {
        try { lastFocused.focus({ preventScroll: true }); } catch (_) { lastFocused.focus(); }
      }
    }

    function choose(granted) {
      writeChoice(granted ? 'granted' : 'denied');
      applyConsent(granted);
      dismiss();
    }

    if (acceptBtn) acceptBtn.addEventListener('click', function () { choose(true); });
    if (rejectBtn) rejectBtn.addEventListener('click', function () { choose(false); });

    /* Esc → decline (the privacy-safe default; never a hidden "accept"). */
    banner.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        e.preventDefault();
        choose(false);
      }
    });

    show();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
